import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
    Modal,
    Box,
    Typography,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Button,
    CircularProgress,
    Paper,
    Pagination,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    TableSortLabel,
    TextField,
    InputAdornment,
} from '@mui/material';
import { Close, ArrowBack, Autorenew, Search } from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { getDailyPLSummary, getDailyPLUserData } from '../redux/slices/operator';
import modifyScriptName from '../utils/modifyScriptName';

const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: { xs: '95%', sm: '90%', md: '85%', lg: '75%', xl: '65%' },
    maxHeight: '85vh',
    bgcolor: 'background.paper',
    borderRadius: 2,
    boxShadow: 24,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
};

export default function DailyOpenPLModal({ open, onClose }) {
    const dispatch = useDispatch();
    const [isDrilledDown, setIsDrilledDown] = useState(false);
    const [selectedScript, setSelectedScript] = useState(null);
    const [page, setPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(20);
    const [sortKey, setSortKey] = useState('totalDailyPL');
    const [sortOrder, setSortOrder] = useState('desc');
    const [lastRefreshTime, setLastRefreshTime] = useState(new Date());
    const [nextRefreshIn, setNextRefreshIn] = useState(60);
    const [search, setSearch] = useState('');
    const [userSearch, setUserSearch] = useState('');

    const {
        dailyPLSummary: {
            data: scriptData,
            isLoading: scriptLoading,
            page: currentPage,
            limit: currentLimit,
            totalPages,
            totalItems,
            sortKey: currentSortKey,
            sortOrder: currentSortOrder,
        },
        dailyPLUserData: { data: userData, scriptName, isLoading: userLoading },
    } = useSelector((state) => state.operator);

    // Debounced search effect
    useEffect(() => {
        if (!open || isDrilledDown) return;

        const debounceTimer = setTimeout(() => {
            dispatch(getDailyPLSummary({
                page,
                limit: rowsPerPage,
                sortKey,
                sortOrder,
                search,
            }));
            setLastRefreshTime(new Date());
            setNextRefreshIn(60);
        }, 500); // 500ms debounce

        return () => clearTimeout(debounceTimer);
    }, [dispatch, open, page, rowsPerPage, sortKey, sortOrder, search, isDrilledDown]);

    // Auto-refresh every 60 seconds
    useEffect(() => {
        if (!open || isDrilledDown) return;

        const refreshInterval = setInterval(() => {
            dispatch(getDailyPLSummary({
                page,
                limit: rowsPerPage,
                sortKey,
                sortOrder,
                search,
            }));
            setLastRefreshTime(new Date());
            setNextRefreshIn(60);
        }, 60000); // 60 seconds

        const countdownInterval = setInterval(() => {
            setNextRefreshIn((prev) => (prev > 0 ? prev - 1 : 60));
        }, 1000); // Update countdown every second

        return () => {
            clearInterval(refreshInterval);
            clearInterval(countdownInterval);
        };
    }, [dispatch, open, isDrilledDown, page, rowsPerPage, sortKey, sortOrder, search]);

    // Cleanup when modal closes
    useEffect(() => {
        if (!open) {
            setIsDrilledDown(false);
            setSelectedScript(null);
            setPage(1);
            setSearch('');
            setUserSearch('');
        }
    }, [open]);

    const handleScriptClick = (scriptData) => {
        setSelectedScript(scriptData);
        setIsDrilledDown(true);
        const scriptId = scriptData.scriptId || scriptData.script?._id;
        const scriptName = scriptData.script?.name || scriptData.scriptName;
        const expiry = scriptData.script?.expiry || scriptData.expiry;
        dispatch(getDailyPLUserData(scriptId, scriptName, expiry, 100));
    };

    const handleBack = () => {
        setIsDrilledDown(false);
        setSelectedScript(null);
    };

    const handlePageChange = (event, newPage) => {
        setPage(newPage);
    };

    const handleRowsPerPageChange = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(1); // Reset to first page when changing rows per page
    };

    const handleSortChange = (newSortKey) => {
        if (sortKey === newSortKey) {
            // Toggle sort order if same column
            setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
        } else {
            // Set new sort key and default to descending
            setSortKey(newSortKey);
            setSortOrder('desc');
        }
        setPage(1); // Reset to first page when sorting changes
    };

    const handleSearchChange = (event) => {
        setSearch(event.target.value);
        setPage(1); // Reset to first page when searching
    };

    const handleUserSearchChange = (event) => {
        setUserSearch(event.target.value);
    };

    // Filter user data based on search
    const filteredUserData = userData?.filter((row) => {
        if (!userSearch) return true;
        const searchLower = userSearch.toLowerCase();
        const username = (row.user?.username || row.username || '').toLowerCase();
        const clientName = (row.user?.firstname ? `${row.user.firstname} ${row.user.lastname || ''}`.trim() : (row.clientName || '')).toLowerCase();
        const scriptName = (row.script?.tradingsymbol || row.tradingSymbol || '').toLowerCase();
        return username.includes(searchLower) || clientName.includes(searchLower) || scriptName.includes(searchLower);
    }) || [];

    // Calculate totals
    const calculateTotal = (data, field) => {
        if (!data || data.length === 0) return 0;
        return data.reduce((sum, item) => sum + (Number(item[field]) || 0), 0);
    };

    const scriptTotalOpenPL = calculateTotal(scriptData, 'openPL');
    const scriptTotalDailyPL = calculateTotal(scriptData, 'totalDailyPL');
    const userTotalOpenPL = calculateTotal(userData, 'openPL');
    const userTotalDailyPL = calculateTotal(userData, 'totalDailyPL');

    // Format number in Indian format
    const formatValue = (value) => {
        const num = Number(value) || 0;
        return Math.round(num).toLocaleString('en-IN');
    };

    // Format value with color
    const getColoredValue = (value) => {
        const num = Number(value) || 0;
        const color = num >= 0 ? '#2E7D32' : '#C62828';
        return { value: formatValue(value), color };
    };



    const renderScriptTable = () => {
        if (scriptLoading) {
            return (
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
                    <CircularProgress />
                </Box>
            );
        }

        if (!scriptData || scriptData.length === 0) {
            return (
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
                    <Typography variant="body1" color="text.secondary">
                        {search ? 'No results found for your search' : 'No data available'}
                    </Typography>
                </Box>
            );
        }

        return (
            <>
                {/* Search Bar */}
                <Box sx={{ mb: 2 }}>
                    <TextField
                        fullWidth
                        size="small"
                        placeholder="Search by script name..."
                        value={search}
                        onChange={handleSearchChange}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Search />
                                </InputAdornment>
                            ),
                        }}
                    />
                </Box>

                <TableContainer component={Paper} sx={{ maxHeight: 'calc(85vh - 300px)', border: '1px solid #E0E0E0' }}>
                    <Table stickyHeader size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 700, bgcolor: 'primary.main', color: 'black', fontSize: '0.85rem', position: 'sticky', left: 0, zIndex: 3, boxShadow: '2px 0 5px -2px rgba(0,0,0,0.1)' }}>Script</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 700, bgcolor: 'primary.main', color: 'black', fontSize: '0.85rem' }}>
                                    <TableSortLabel
                                        active={sortKey === 'openQuantity'}
                                        direction={sortKey === 'openQuantity' ? sortOrder : 'desc'}
                                        onClick={() => handleSortChange('openQuantity')}
                                        sx={{ color: 'black !important', '& .MuiTableSortLabel-icon': { color: 'black !important' } }}
                                    >
                                        Qty
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell align="right" sx={{ fontWeight: 700, bgcolor: 'primary.main', color: 'black', fontSize: '0.85rem' }}>
                                    <TableSortLabel
                                        active={sortKey === 'openPL'}
                                        direction={sortKey === 'openPL' ? sortOrder : 'desc'}
                                        onClick={() => handleSortChange('openPL')}
                                        sx={{ color: 'black !important', '& .MuiTableSortLabel-icon': { color: 'black !important' } }}
                                    >
                                        Open PL
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell align="right" sx={{ fontWeight: 700, bgcolor: 'primary.main', color: 'black', fontSize: '0.85rem' }}>Price Diff</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 700, bgcolor: 'primary.main', color: 'black', fontSize: '0.85rem' }}>Open Price</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 700, bgcolor: 'primary.main', color: 'black', fontSize: '0.85rem' }}>Current Price</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 700, bgcolor: 'primary.main', color: 'black', fontSize: '0.85rem' }}>
                                    <TableSortLabel
                                        active={sortKey === 'closedPL'}
                                        direction={sortKey === 'closedPL' ? sortOrder : 'desc'}
                                        onClick={() => handleSortChange('closedPL')}
                                        sx={{ color: 'black !important', '& .MuiTableSortLabel-icon': { color: 'black !important' } }}
                                    >
                                        Closed PL
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell align="right" sx={{ fontWeight: 700, bgcolor: 'primary.main', color: 'black', fontSize: '0.85rem' }}>
                                    <TableSortLabel
                                        active={sortKey === 'totalDailyPL'}
                                        direction={sortKey === 'totalDailyPL' ? sortOrder : 'desc'}
                                        onClick={() => handleSortChange('totalDailyPL')}
                                        sx={{ color: 'black !important', '& .MuiTableSortLabel-icon': { color: 'black !important' } }}
                                    >
                                        Today Gross
                                    </TableSortLabel>
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {scriptData.map((row, index) => {
                                const openPLColor = getColoredValue(row.openPL);
                                const closedPLColor = getColoredValue(row.closedPL);
                                const dailyPLColor = getColoredValue(row.totalDailyPL);

                                // Calculate Price Diff (Current Price - Open Price)
                                const priceDiff = (row.currentPrice || 0) - (row.ohlcOpen || 0);
                                const priceDiffColor = priceDiff >= 0 ? '#2E7D32' : '#C62828';
                                const priceDiffArrow = priceDiff >= 0 ? '▲' : '▼';

                                // Qty color based on positive/negative value
                                const qty = row.openQuantity || row.openQty || 0;
                                const qtyColor = qty >= 0 ? '#2E7D32' : '#C62828';
                                const qtyValue = formatValue(Math.abs(qty));

                                return (
                                    <TableRow
                                        key={index}
                                        hover
                                        sx={{
                                            '&:hover': { bgcolor: 'action.hover' },
                                            '& td': { borderRight: '1px solid #E0E0E0', fontSize: '0.85rem', py: 1 },
                                        }}
                                    >
                                        <TableCell
                                            sx={{
                                                fontWeight: 600,
                                                position: 'sticky',
                                                left: 0,
                                                bgcolor: 'inherit',
                                                zIndex: 1,
                                                boxShadow: '2px 0 5px -2px rgba(0,0,0,0.1)',
                                            }}
                                        >
                                            <Button
                                                size="small"
                                                onClick={() => handleScriptClick(row)}
                                                sx={{
                                                    textTransform: 'none',
                                                    color: 'primary.main',
                                                    fontWeight: 600,
                                                    fontSize: '0.85rem',
                                                    p: 0,
                                                    minWidth: 'auto',
                                                    '&:hover': {
                                                        bgcolor: 'transparent',
                                                        textDecoration: 'underline',
                                                    },
                                                }}
                                            >
                                                {modifyScriptName(row.script?.tradingsymbol || row.tradingSymbol, row.script?.expiry)}
                                            </Button>
                                        </TableCell>
                                        <TableCell align="right" sx={{ color: qtyColor, fontWeight: 600 }}>
                                            {qty >= 0 ? '' : '-'}{qtyValue}
                                        </TableCell>
                                        <TableCell align="right" sx={{ color: openPLColor.color, fontWeight: 600 }}>
                                            {openPLColor.value}
                                        </TableCell>
                                        <TableCell align="right">
                                            <Typography variant="body2" sx={{ fontWeight: 600, color: priceDiffColor, fontSize: '0.85rem' }}>
                                                {priceDiffArrow} {priceDiff >= 0 ? formatValue(priceDiff) : formatValue(Math.abs(priceDiff))}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="right">{formatValue(row.ohlcOpen)}</TableCell>
                                        <TableCell align="right">{formatValue(row.currentPrice)}</TableCell>
                                        <TableCell align="right" sx={{ color: closedPLColor.color, fontWeight: 600 }}>
                                            {closedPLColor.value}
                                        </TableCell>
                                        <TableCell align="right" sx={{ color: dailyPLColor.color, fontWeight: 700 }}>
                                            {dailyPLColor.value}
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>

                {/* Pagination Controls */}
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mt: 2,
                        px: 2,
                        flexWrap: 'wrap',
                        gap: 2,
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <FormControl size="small" sx={{ minWidth: 120 }}>
                            <InputLabel id="rows-per-page-label">Rows per page</InputLabel>
                            <Select
                                labelId="rows-per-page-label"
                                value={rowsPerPage}
                                label="Rows per page"
                                onChange={handleRowsPerPageChange}
                            >
                                <MenuItem value={10}>10</MenuItem>
                                <MenuItem value={20}>20</MenuItem>
                                <MenuItem value={50}>50</MenuItem>
                                <MenuItem value={100}>100</MenuItem>
                            </Select>
                        </FormControl>
                        <Typography variant="body2" color="text.secondary">
                            Showing {((currentPage - 1) * currentLimit) + 1} - {Math.min(currentPage * currentLimit, totalItems)} of {totalItems} results
                        </Typography>
                    </Box>
                    <Pagination
                        count={totalPages}
                        page={currentPage}
                        onChange={handlePageChange}
                        color="primary"
                        size="medium"
                        showFirstButton
                        showLastButton
                    />
                </Box>
            </>
        );
    };

    const renderUserTable = () => {
        if (userLoading) {
            return (
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
                    <CircularProgress />
                </Box>
            );
        }

        if (!userData || userData.length === 0) {
            return (
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
                    <Typography variant="body1" color="text.secondary">
                        No user data available for this script
                    </Typography>
                </Box>
            );
        }

        if (filteredUserData.length === 0) {
            return (
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
                    <Typography variant="body1" color="text.secondary">
                        No results found for your search
                    </Typography>
                </Box>
            );
        }

        return (
            <>
                {/* Search Bar */}
                <Box sx={{ mb: 2 }}>
                    <TextField
                        fullWidth
                        size="small"
                        placeholder="Search by user, client name or script..."
                        value={userSearch}
                        onChange={handleUserSearchChange}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Search />
                                </InputAdornment>
                            ),
                        }}
                    />
                </Box>

                <TableContainer component={Paper} sx={{ maxHeight: 'calc(85vh - 240px)', border: '1px solid #E0E0E0' }}>
                    <Table stickyHeader size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 700, bgcolor: 'primary.main', color: 'black', fontSize: '0.85rem', position: 'sticky', left: 0, zIndex: 3, boxShadow: '2px 0 5px -2px rgba(0,0,0,0.1)', width: 100, minWidth: 100, maxWidth: 100 }}>User</TableCell>
                                <TableCell sx={{ fontWeight: 700, bgcolor: 'primary.main', color: 'black', fontSize: '0.85rem', position: 'sticky', left: 100, zIndex: 3, boxShadow: '2px 0 5px -2px rgba(0,0,0,0.1)', width: 110, minWidth: 110, maxWidth: 110 }}>Script</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 700, bgcolor: 'primary.main', color: 'black', fontSize: '0.85rem' }}>Qty</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 700, bgcolor: 'primary.main', color: 'black', fontSize: '0.85rem' }}>Open PL</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 700, bgcolor: 'primary.main', color: 'black', fontSize: '0.85rem' }}>Price Diff</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 700, bgcolor: 'primary.main', color: 'black', fontSize: '0.85rem' }}>Open Price</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 700, bgcolor: 'primary.main', color: 'black', fontSize: '0.85rem' }}>Current Price</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 700, bgcolor: 'primary.main', color: 'black', fontSize: '0.85rem' }}>Closed PL</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 700, bgcolor: 'primary.main', color: 'black', fontSize: '0.85rem' }}>Today Gross</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredUserData.map((row, index) => {
                                const openPLColor = getColoredValue(row.openPL);
                                const closedPLColor = getColoredValue(row.closedPL);
                                const dailyPLColor = getColoredValue(row.totalDailyPL);

                                const username = row.user?.username || row.username || row.client?.username || '';
                                const clientName = row.user?.firstname ? `${row.user.firstname} ${row.user.lastname || ''}`.trim() : (row.clientName || row.client?.name || '');
                                const userDisplay = clientName ? `${username} (${clientName})` : username;

                                // Calculate Price Diff (Current Price - Open Price)
                                const openPrice = row.ohlcOpen || row.openPrice || 0;
                                const currentPrice = row.currentPrice || 0;
                                const priceDiff = currentPrice - openPrice;
                                const priceDiffColor = priceDiff >= 0 ? '#2E7D32' : '#C62828';
                                const priceDiffArrow = priceDiff >= 0 ? '▲' : '▼';

                                // Qty color based on positive/negative value
                                const qty = row.openQuantity || row.openQty || 0;
                                const qtyColor = qty >= 0 ? '#2E7D32' : '#C62828';
                                const qtyValue = formatValue(Math.abs(qty));

                                return (
                                    <TableRow
                                        key={index}
                                        hover
                                        sx={{
                                            '&:hover': { bgcolor: 'action.hover' },
                                            '& td': { borderRight: '1px solid #E0E0E0', fontSize: '0.85rem', py: 1 },
                                        }}
                                    >
                                        <TableCell
                                            sx={{
                                                fontWeight: 600,
                                                position: 'sticky',
                                                left: 0,
                                                bgcolor: 'inherit',
                                                zIndex: 1,
                                                boxShadow: '2px 0 5px -2px rgba(0,0,0,0.1)',
                                                width: 100,
                                                minWidth: 100,
                                                maxWidth: 100,
                                            }}
                                        >
                                            {userDisplay}
                                        </TableCell>
                                        <TableCell
                                            sx={{
                                                fontWeight: 600,
                                                position: 'sticky',
                                                left: 100,
                                                bgcolor: 'inherit',
                                                zIndex: 1,
                                                boxShadow: '2px 0 5px -2px rgba(0,0,0,0.1)',
                                                width: 110,
                                                minWidth: 110,
                                                maxWidth: 110,
                                            }}
                                        >
                                            {modifyScriptName(row.script?.tradingsymbol || row.tradingSymbol || '', row.script?.expiry)}
                                        </TableCell>
                                        <TableCell align="right" sx={{ color: qtyColor, fontWeight: 600 }}>
                                            {qty >= 0 ? '' : '-'}{qtyValue}
                                        </TableCell>
                                        <TableCell align="right" sx={{ color: openPLColor.color, fontWeight: 600 }}>
                                            {openPLColor.value}
                                        </TableCell>
                                        <TableCell align="right">
                                            <Typography variant="body2" sx={{ fontWeight: 600, color: priceDiffColor, fontSize: '0.85rem' }}>
                                                {priceDiffArrow} {priceDiff >= 0 ? formatValue(priceDiff) : formatValue(Math.abs(priceDiff))}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="right">{formatValue(openPrice)}</TableCell>
                                        <TableCell align="right">{formatValue(currentPrice)}</TableCell>
                                        <TableCell align="right" sx={{ color: closedPLColor.color, fontWeight: 600 }}>
                                            {closedPLColor.value}
                                        </TableCell>
                                        <TableCell align="right" sx={{ color: dailyPLColor.color, fontWeight: 700 }}>
                                            {dailyPLColor.value}
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            </>
        );
    };

    return (
        <Modal
            open={open}
            onClose={onClose}
            aria-labelledby="daily-open-pl-modal"
            aria-describedby="daily-open-pl-description"
        >
            <Box sx={modalStyle}>
                {/* Header */}
                <Box
                    sx={{
                        px: 3,
                        py: 1.5,
                        borderBottom: '2px solid',
                        borderColor: 'divider',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        bgcolor: 'primary.lighter',
                    }}
                >
                    <Box display="flex" alignItems="center" gap={2}>
                        <Typography variant="h5" fontWeight={700} color="primary.dark">
                            {isDrilledDown ? 'User Daily Open PL' : 'Day to Day Open PL'}
                        </Typography>
                        {!isDrilledDown && (
                            <Box display="flex" alignItems="center" gap={0.5}>
                                <Autorenew sx={{ fontSize: 16, color: 'primary.main' }} />
                                <Typography variant="caption" color="text.secondary">
                                    Refreshing in {nextRefreshIn}s
                                </Typography>
                            </Box>
                        )}
                    </Box>
                    <IconButton onClick={onClose} size="small">
                        <Close />
                    </IconButton>
                </Box>

                {/* Content */}
                <Box
                    sx={{
                        flex: 1,
                        overflow: 'auto',
                        p: 1.5,
                    }}
                >
                    {isDrilledDown ? renderUserTable() : renderScriptTable()}
                </Box>

                {/* Footer */}
                <Box
                    sx={{
                        px: 2,
                        py: 1,
                        borderTop: '1px solid',
                        borderColor: 'divider',
                        display: 'flex',
                        justifyContent: isDrilledDown ? 'space-between' : 'flex-end',
                        gap: 1.5,
                        bgcolor: 'grey.50',
                    }}
                >
                    {isDrilledDown && (
                        <Button
                            variant="outlined"
                            startIcon={<ArrowBack />}
                            onClick={handleBack}
                            size="small"
                            sx={{
                                fontWeight: 600,
                                textTransform: 'none',
                            }}
                        >
                            Back
                        </Button>
                    )}
                    <Button variant="outlined" size="small" onClick={onClose}>
                        Close
                    </Button>
                </Box>
            </Box>
        </Modal>
    );
}

DailyOpenPLModal.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
};
